import _ from 'underscore';
import React, { useCallback, useMemo, useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import {
  useImageExperienceAdPreviewModel,
  AdPreviewCarouselLayoutEx,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

import {
  AdPreviewDataProps,
  AdPreviewSurface,
  adPreviewConfig,
  extraPlacementInfo,
} from '../const';
import { AdAssetsPreviewerControls } from './ad-assets-previewer-controls';
import {
  getImagesToDisplay,
  getNumberOfPlacementBySurface,
} from './ad-assets-previewer-utils';
import { getAdAssetsPreviewerStyles } from './ad-assets-previewer-uapl-styles';

import DefaultImage from '../media/default-image.png';

export const AdAssetsPreviewerUAPL = (props) => {
  const {
    i18n,
    imageListState: {
      imageList,
      selectedImageId,
    },
    onSetCropImageObj,
    onSetEditingPlacementType,
    onRenderEstimations,
  } = props;

  const hasEstimations = _.isFunction(onRenderEstimations);

  const [selectedSurface, setSelectedSurface] = useState(AdPreviewSurface.MSN);

  const stylingProps = useMemo(() =>
    ({ component: { placements: getNumberOfPlacementBySurface(selectedSurface) } }), [selectedSurface]);
  const getStyles = useResponsiveStyles(getAdAssetsPreviewerStyles, stylingProps);

  const adDataFromProps = _.pick(props, AdPreviewDataProps);
  const [adData, setAdData] = useState(adDataFromProps);

  const hasAdDataChanged = useCallback(newAdData => !_.isMatch(newAdData, adData), [adData]);
  const debouncedSetAdData = useMemo(() => _.debounce(setAdData, 200), []);

  if (hasAdDataChanged(adDataFromProps)) {
    debouncedSetAdData.cancel();
    debouncedSetAdData(adDataFromProps);
  }

  const imagesToDisplay = useMemo(() => getImagesToDisplay(imageList, selectedImageId), [imageList, selectedImageId]);

  const onEdit = useCallback((type, placementType) => {
    const cropImageObj = _.find(imagesToDisplay, imagePlacement => imagePlacement.type === type);
    onSetCropImageObj(cropImageObj);
    onSetEditingPlacementType(placementType);
  }, [imagesToDisplay, onSetCropImageObj, onSetEditingPlacementType]);

  const {
    onUpdateImageExperienceImageList,
    singleImagePlacementSets: placementSets,
  } = useImageExperienceAdPreviewModel(adPreviewConfig, DefaultImage);

  useEffect(() => {
    onUpdateImageExperienceImageList([imagesToDisplay], null);
  }, [imagesToDisplay, onUpdateImageExperienceImageList]);

  const curPlacementList = useMemo(() => {
    const { placementList: pl } =
      _.find(placementSets, ({ placementSetName }) => placementSetName === selectedSurface) || {};
    return pl;
  }, [placementSets, selectedSurface]);

  const onRenderPlacement = useCallback((placement, AdPlacementComponent) => {
    const { imageMeta, placementMeta } = placement || {};
    const { placementType } = placementMeta || {};
    const isPlaceholderImage = imageMeta && (imageMeta.url === DefaultImage);

    const { customClassName, imagePreviewHeight } = extraPlacementInfo[placementType] || {};

    return (
      <div className={classNames(getStyles('placementContainerClassName'))}>
        <AdPlacementComponent
          isEnabled={!isPlaceholderImage}
          placementData={placement}
          {...adData}
          finalUrl={adData.url}
          i18n={i18n}
          customClassName={customClassName}
          imagePreviewHeight={imagePreviewHeight}
        />
      </div>
    );
  }, [adData, getStyles, i18n]);

  const onRenderCarouselHeader = useCallback((placement) => {
    const { placementMeta, hasImageExperienceData } = placement || {};
    const { imageType, placementType } = placementMeta || {};
    if (!placementType) {
      return null;
    }
    const { aspectRatioUIString, previewTitle } = extraPlacementInfo[placementType] || {};
    return (
      <Fragment>
        {hasEstimations &&
          <div className={classNames(getStyles('carouselLayoutHeaderExtra'))}>
            {onRenderEstimations()}
            <AdAssetsPreviewerControls
              selectedSurface={selectedSurface}
              onSurfaceChange={setSelectedSurface}
            />
          </div>
        }
        <div className={classNames(getStyles('carouselLayoutHeader'))}>
          <div className="carousel-header-texts">
            <span className="carousel-header-ratio">{aspectRatioUIString}</span>
            {previewTitle && <span className="carousel-header-title" title={i18n.getString(previewTitle)}>{i18n.getString(previewTitle)}</span>}
          </div>
          {hasImageExperienceData && (
            <div className="ad-preview-actions">
              <button onClick={() => onEdit(imageType, placementType)} className="btn btn-default edit-image-button">
                {i18n.getString(_TL_('Edit aspect ratio'))}
              </button>
            </div>
          )}
        </div>
      </Fragment>
    );
  }, [getStyles, hasEstimations, i18n, onEdit, onRenderEstimations, selectedSurface]);

  return (
    <div className={classNames('ad-assets-previewer', getStyles('container'))}>
      {!hasEstimations && <AdAssetsPreviewerControls
        selectedSurface={selectedSurface}
        onSurfaceChange={setSelectedSurface}
      />
      }
      <AdPreviewCarouselLayoutEx
        placementList={curPlacementList}
        onRenderPlacement={onRenderPlacement}
        onRenderHeader={onRenderCarouselHeader}
        enableAutoPlay={false}
        i18n={i18n}
        customCarouselRootClassName={classNames(getStyles('customCarouselClassName'))}
        resetOn={selectedSurface}
      />
      <div className={classNames(getStyles('disclaimerMessage'))}>
        {i18n.getString(_TL_('These are examples of how your ads could appear. Depending on placement, the actual ad may appear differently.'))}
      </div>
    </div>
  );
};

AdAssetsPreviewerUAPL.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  imageListState: PropTypes.shape({
    imageList: PropTypes.arrayOf(PropTypes.shape({})),
    selectedImageId: PropTypes.number,
  }).isRequired,
  onSetCropImageObj: PropTypes.func.isRequired,
  onSetEditingPlacementType: PropTypes.func.isRequired,
  onRenderEstimations: PropTypes.func,
};

AdAssetsPreviewerUAPL.defaultProps = {
  onRenderEstimations: undefined,
};
